/**
 * n8n Webhook Service
 * Handles submitting lead, support, and feedback forms to the n8n webhook workflow.
 */

export type N8nFormType = 'lead' | 'support' | 'feedback';

export interface N8nFormDimensions {
  idea?: string | null;
  music?: string | null;
  video?: string | null;
}

export interface N8nFormSubmission {
  id: string; // UUID v4
  type: N8nFormType;
  source: string;
  created_at: string; // ISO timestamp (e.g. 2026-08-25T20:00:00.000Z)

  name?: string | null;
  email?: string | null;
  phone?: string | null;

  user_id?: string | null;
  user_role?: string | null;

  clinic_id?: string | null;
  clinic_name?: string | null;
  organization_name?: string | null;
  address?: string | null;
  zip_code?: string | null;

  session_id?: string | null;
  page?: string | null;
  topic?: string | null;
  message?: string | null;

  content_id?: string | null;
  content_url?: string | null;
  previous_content_id?: string | null;
  previous_content_url?: string | null;
  survey_id?: string | null;
  previous_survey_id?: string | null;
  batch_id?: string | null;

  rating?: number | null; // int (1-5)
  overall_feedback?: string | null;
  decision?: 'approved' | 'rejected' | null;
  dimensions?: N8nFormDimensions | null;
}

export interface SubmitResult {
  success: boolean;
  message?: string;
  data?: any;
  error?: string;
}

/**
 * Generates a standard UUID v4 string.
 */
export function generateUUID(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback RFC4122 v4 UUID generator
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Forms go through our own serverless proxy (api/submit-form.js), which adds the n8n API key server-side.
 */
export const FORM_ENDPOINT = '/api/submit-form';

/**
 * The n8n workflow fails validation when a contact field is missing or empty (and trims a lone space to empty),
 * so lead and support submissions always carry every contact field. Anything the form didn't fill in gets a
 * placeholder of the right kind: text -> "-", numeric-looking columns (phone, zip) -> "0", email -> a noreply address.
 */
const TEXT_PLACEHOLDER = '-';
const NUMERIC_PLACEHOLDER = '0';
const EMAIL_PLACEHOLDER = 'noreply@quilt.gg';

const isBlank = (v: unknown) => v === undefined || v === null || (typeof v === 'string' && v.trim() === '');

function withPlaceholders(
  payload: Partial<N8nFormSubmission> & { type: N8nFormType; source: string }
): Partial<N8nFormSubmission> & { type: N8nFormType; source: string } {
  if (payload.type !== 'lead' && payload.type !== 'support') return payload;

  const filled = { ...payload };
  // clinic and organization are the same thing; reuse whichever one the form collected
  const org = !isBlank(payload.organization_name) ? payload.organization_name : payload.clinic_name;
  if (isBlank(filled.organization_name) && !isBlank(org)) filled.organization_name = org;
  if (isBlank(filled.clinic_name) && !isBlank(org)) filled.clinic_name = org;

  const textFields = ['name', 'clinic_name', 'organization_name', 'address', 'topic', 'message'] as const;
  for (const key of textFields) if (isBlank(filled[key])) filled[key] = TEXT_PLACEHOLDER;
  for (const key of ['phone', 'zip_code'] as const) if (isBlank(filled[key])) filled[key] = NUMERIC_PLACEHOLDER;
  if (isBlank(filled.email)) filled.email = EMAIL_PLACEHOLDER;
  return filled;
}

/**
 * Submits a form payload to the n8n webhook workflow.
 * Automatically injects id, created_at, and current page if not already provided.
 */
export async function submitN8nForm(
  rawPayload: Partial<N8nFormSubmission> & { type: N8nFormType; source: string }
): Promise<SubmitResult> {
  const payload = withPlaceholders(rawPayload);
  const cleanPayload: Record<string, any> = {
    id: payload.id || generateUUID(),
    type: payload.type,
    source: payload.source,
    created_at: payload.created_at || new Date().toISOString(),
  };

  const candidateFields: (keyof N8nFormSubmission)[] = [
    'name',
    'email',
    'phone',
    'user_id',
    'user_role',
    'clinic_id',
    'clinic_name',
    'organization_name',
    'address',
    'zip_code',
    'session_id',
    'page',
    'topic',
    'message',
    'content_id',
    'content_url',
    'previous_content_id',
    'previous_content_url',
    'survey_id',
    'previous_survey_id',
    'batch_id',
    'rating',
    'overall_feedback',
    'decision',
    'dimensions',
  ];

  for (const key of candidateFields) {
    const val = payload[key];
    if (val !== undefined && val !== null && val !== '') {
      cleanPayload[key] = val;
    }
  }

  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    const response = await fetch(FORM_ENDPOINT, {
      method: 'POST',
      headers,
      body: JSON.stringify(cleanPayload),
    });


    if (!response.ok) {
      let errorText = `HTTP Error ${response.status}: ${response.statusText}`;
      try {
        const errorJson = await response.json();
        if (errorJson?.message) {
          errorText = errorJson.message;
        }
      } catch {
        // Response wasn't JSON
      }
      return {
        success: false,
        error: errorText,
      };
    }

    let data: any = null;
    try {
      data = await response.json();
    } catch {
      data = { status: 'ok' };
    }

    return {
      success: true,
      message: 'Submission successful',
      data,
    };
  } catch (err: any) {
    console.error('n8n Form Submission Error:', err);
    return {
      success: false,
      error: err?.message || 'Network error occurred while submitting form.',
    };
  }
}
